import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '@/api/apiCall';
import { toast } from 'sonner';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import { useQueue } from '@/context/QueueContext';
import { API_CALL_TIME } from '@/constants/constant';

// ─── Fallback / Default Data ──────────────────────────────────────────────────
const defaultData = {
  profileTable: {
    table_key: 'bilal_real_estate_profile',
    title: 'Bilal Real Estate - Profile',
    data: {
      project_name:    { label: 'Project Name', value: 'Bilal Market' },
      contract_term:   { label: 'Contract Term', value: 'Our own Building' },
      contract_period: { start_date: '29.03.2017', end_date: '29.06.2041' },
    },
  },
  floorsTable: {
    table_key: 'bilal_building_floors_breakdown',
    title: 'Bilal Building Floor Breakdown',
    data: {
      columns: [
        { column_key: 'building_floor', label: 'BUILDING FLOOR', type: 'text' },
        {
          column_key: 'floor_details',
          label: '2',
          type: 'group',
          sub_columns: [
            { column_key: 'floor_number', label: '', type: 'text' },
            { column_key: 'floor_type',   label: '', type: 'text' },
          ],
        },
        { column_key: 'qty', label: 'QTY', type: 'text' },
      ],
      rows: [
        { building_floor: '', floor_number: 'M Floor / MF',   floor_type: 'SHOPS', qty: '120', building_rowSpan: 2 },
        { building_floor: '', floor_number: '1st FLOOR / BF', floor_type: 'SHOPS', qty: '101' },
      ],
    },
  },
};

// ─── Normalize GET response → internal state ──────────────────────────────────
const normalizeResponse = (apiData) => {
  if (!apiData || apiData.length === 0) return defaultData;
  const find = (key) => apiData.find(t => t.table_key === key);
  return {
    profileTable: find('bilal_real_estate_profile')      || defaultData.profileTable,
    floorsTable:  find('bilal_building_floors_breakdown') || defaultData.floorsTable,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper — fetches data, handles loading/error, then renders ProfileTable
// ─────────────────────────────────────────────────────────────────────────────
const BilalContract = ({ year = '', monthNumber = '', contract = '' }) => {
  const { enqueue } = useQueue();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setProfileData(null);

        const response = await enqueue(() =>
          apiCall('GET', '/boardpack/get-tables-data', { year, contract, month: monthNumber })
        );

        if (!response?.success) throw new Error(response?.message || 'API returned unsuccessful response');

        const rawApiData = Array.isArray(response?.data) && response.data.length > 0
          ? response.data
          : null;

        const normalized = normalizeResponse(rawApiData);
        setProfileData(normalized);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, monthNumber, contract]);

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error || 'Something went wrong'} />;

  return (
    <ProfileTable
      profileData={profileData}
      year={year}
      monthNumber={monthNumber}
      contract={contract}
    />
  );
};

export default BilalContract;

// ─────────────────────────────────────────────────────────────────────────────
// ProfileTable — all state derived from API tables, POST sends them back as-is
// ─────────────────────────────────────────────────────────────────────────────
const ProfileTable = ({ profileData, year, monthNumber, contract }) => {
  const saveTimeoutRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);

  const [profileTable, setProfileTable] = useState(profileData?.profileTable);
  const [floorsTable,  setFloorsTable]  = useState(profileData?.floorsTable);

  useEffect(() => {
    if (profileData) {
      setProfileTable(profileData.profileTable);
      setFloorsTable(profileData.floorsTable);
    }
  }, [profileData]);

  // ── Shorthand accessors ───────────────────────────────────────────────────
  const profileData_ = profileTable?.data || {};
  const floorsData   = floorsTable?.data  || {};

  const projectName  = profileData_?.project_name?.value        || '';
  const contractTerm = profileData_?.contract_term?.value       || '';
  const startDate    = profileData_?.contract_period?.start_date || '';
  const endDate      = profileData_?.contract_period?.end_date   || '';

  const floorColumns = floorsData?.columns || [];
  const floorRows    = floorsData?.rows    || [];

  const floorsCount = floorColumns.find(c => c.type === 'group')?.label || '';

  // ── Auto-save ─────────────────────────────────────────────────────────────
  const autoSave = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(saveToAPI, API_CALL_TIME);
  };

  // ── POST payload — mirrors GET response structure ─────────────────────────
  const buildPostPayload = () => ({
    contract,
    year,
    month: monthNumber,
    created_by: 1,
    tables: [
      { table_key: profileTable?.table_key, title: profileTable?.title, data: profileData_ },
      { table_key: floorsTable?.table_key,  title: floorsTable?.title,  data: floorsData   },
    ],
  });

  const saveToAPI = async () => {
    try {
      setIsSaving(true);
      const response = await apiCall('POST', '/boardpack/add-tables-data', buildPostPayload());
      if (!response?.success) throw new Error(response?.message || 'Failed to save');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleProfileField = (field, subField, value) => {
    setProfileTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: subField
          ? { ...prev.data[field], [subField]: value }
          : value,
      },
    }));
    autoSave();
  };

  const handleFloorsCount = (value) => {
    setFloorsTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        columns: prev.data.columns.map(col =>
          col.type === 'group' ? { ...col, label: value } : col
        ),
      },
    }));
    autoSave();
  };

  const handleRowField = (rowIdx, field, value) => {
    setFloorsTable(prev => ({
      ...prev,
      data: {
        ...prev.data,
        rows: prev.data.rows.map((row, i) =>
          i === rowIdx ? { ...row, [field]: value } : row
        ),
      },
    }));
    autoSave();
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-8 bg-white">

      {/* Page Title */}
      <div className="text-center mb-4">
        <h1 className="text-xl font-bold">{profileTable?.title || 'Bilal Real Estate - Profile'}</h1>
      </div>

      {/* Main content box */}
      <div className="!border h-130 !border-black p-14">

        {/* Activities header */}
        <div className="text-[16px] font-semibold mb-4">Activities:</div>

        {/* Project Name row */}
        <div className="flex items-start gap-2 mb-1 ml-4">
          <div className="w-4 h-4 !border-2 !border-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-[12px]">
            {profileData_?.project_name?.label || 'Project Name'}{' '}
            <strong>
              <input
                type="text"
                value={projectName}
                onChange={e => handleProfileField('project_name', 'value', e.target.value)}
                className="font-bold outline-none bg-transparent !border-0 text-[12px]"
              />
            </strong>
          </div>
        </div>

        {/* Contract Term row */}
        <div className="flex items-start gap-2 mb-6 ml-10">
          <div className="w-3 h-3 !border !border-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-[12px]">
            {profileData_?.contract_term?.label || 'Contract term'}{' '}
            <strong>
              <input
                type="text"
                value={contractTerm}
                onChange={e => handleProfileField('contract_term', 'value', e.target.value)}
                className="font-bold outline-none bg-transparent !border-0 text-[12px] w-26"
              />
            </strong>
            {'; start date '}
            <strong>
              <input
                type="text"
                value={startDate}
                onChange={e => handleProfileField('contract_period', 'start_date', e.target.value)}
                className="font-bold outline-none bg-transparent !border-0 text-[12px] w-17"
              />
            </strong>
            {' to '}
            <strong>
              <input
                type="text"
                value={endDate}
                onChange={e => handleProfileField('contract_period', 'end_date', e.target.value)}
                className="font-bold outline-none bg-transparent !border-0 text-[12px] w-24"
              />
            </strong>
          </div>
        </div>

        {/* Building Floors Table */}
        <div className="ml-10">
          <table className="!border-collapse text-[11px]">
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  className="!border !border-black px-3 py-1 font-bold text-center align-middle"
                  style={{ minWidth: '120px' }}
                >
                  BUILDING FLOOR
                </th>
                <th
                  colSpan={3}
                  className="!border !border-black px-3 py-1 font-bold text-center"
                  style={{ minWidth: '160px' }}
                >
                  <input
                    type="text"
                    value={floorsCount}
                    onChange={e => handleFloorsCount(e.target.value)}
                    className="font-bold outline-none bg-transparent !border-0 text-center w-12 text-[11px]"
                  />
                </th>
                {/* <th
                  rowSpan={2}
                  className="!border !border-black px-3 py-1 font-bold text-center align-middle"
                  style={{ minWidth: '60px' }}
                >
                  QTY
                </th> */}
              </tr>
              {/* <tr>
                <th className="!border !border-black px-2 py-0.5" style={{ minWidth: '120px' }} />
                <th className="!border !border-black px-2 py-0.5" style={{ minWidth: '60px' }} />
              </tr> */}
            </thead>
            <tbody>
              {floorRows.map((row, idx) => (
                <tr key={idx}>
                 {idx === 0 ? (
  <td
    rowSpan={row.building_rowSpan || floorRows.length}
    className="!border !border-black px-2 py-1 text-center align-middle"
  >
    <input
      type="text"
      value={row.building_floor || ''}
      onChange={e => handleRowField(idx, 'building_floor', e.target.value)}
      className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
    />
  </td>
) : null}
                  <td className="!border !border-black px-2 py-1">
                    <input
                      type="text"
                      value={row.floor_number || ''}
                      onChange={e => handleRowField(idx, 'floor_number', e.target.value)}
                      className="outline-none bg-transparent !border-0 w-full text-[11px]"
                    />
                  </td>
                  <td className="!border !border-black px-2 py-1 text-center">
                    <input
                      type="text"
                      value={row.floor_type || ''}
                      onChange={e => handleRowField(idx, 'floor_type', e.target.value)}
                      className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
                    />
                  </td>
                  <td className="!border !border-black px-2 py-1 text-center">
                    <input
                      type="text"
                      value={row.qty || ''}
                      onChange={e => handleRowField(idx, 'qty', e.target.value)}
                      className="outline-none bg-transparent !border-0 w-full text-center text-[11px]"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      {/* Save indicator */}
      {/* {isSaving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white text-xs px-3 py-1.5 rounded shadow-lg z-50">
          Saving...
        </div>
      )} */}
    </div>
  );
};